import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/stocks.png';
import { Database, Zap, Globe, Clock, TrendingDown, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/stocks');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/stock-market-pulse-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/market-breadth-vehicle-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/stock-factor-rotation-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/financial-quality-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const StocksPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Stock Price API: Daily & Historical Data"
        description="Get daily and hourly stock price data from 85+ global exchanges with an OHLC & candlestick API, decades of history back to 1987, and 100,000+ symbols."
        canonicalUrl="https://axionquant.com/products/stocks"
        ogType="product"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "AxionQuant Stock Price API",
          "description": "Daily and hourly stock price data from 85+ global exchanges. Covers 100,000+ symbols with OHLC bars, full history back to 1987, corporate actions, and insider trading.",
          "brand": {
            "@type": "Brand",
            "name": "AxionQuant"
          },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Stock Market API",
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "29",
            "highPrice": "99",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "description": "Free tier available; paid plans from $29/month"
          }
        })}</script>
      </Helmet>

      <StyledProducts>


     <Header
        docs="/docs/01-market-data/stocks"
        title="Historical Stock Price API"
        body="Daily and hourly OHLC and candlestick data from global exchanges, with decades of history back to 1987. Power long-horizon research, backtesting, portfolio analytics, and systematic research with an institutional-grade stock market API."
        crumb={{name: 'Stocks', path:'/products/stocks'}}
  tabs={[
    {
      label: 'Price & Candlesticks',
      language: 'python',
      filename: 'price_candles.py',
      bg: '24.jpg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

df = utils.df(
    client.stocks.prices(
        "NVDA",
        from_date=utils.d(utils.yearago),
        frame="daily",
    )
)

visualize.candles(df)
`,
    },
    {
      label: 'Multi-Asset Comparison',
      language: 'python',
      filename: 'multi_asset_comparison.py',
      bg: '23.jpg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

tickers = ["AAPL", "MSFT", "GOOGL", "META", "NVDA"]
from_dt = utils.d(utils.yearago)

prices = {
    t: utils.df(client.stocks.prices(t, from_date=from_dt))
    for t in tickers
}

df = utils.snap(
    [prices[t] for t in tickers],
    names=tickers,
    overwrite=["close"],
    col="time",
)

df = utils.relativity(df, cols=tickers)

visualize.polls(df)
`,
    },
    {
      label: 'Technical Signals',
      language: 'python',
      filename: 'technical_signals.py',
      bg: '22.jpg',
      code: `from axion import Axion, utils, visualize, ta

client = Axion(api_key="your_api_key")

df = utils.df(
    client.stocks.prices("AAPL", from_date=utils.d(utils.yearago))
)

df["ema_21"]  = ta.ema(df, period=21)
df["ema_50"]  = ta.ema(df, period=50)
df["rsi"]     = ta.rsi(df, period=14)

macd_line, signal_line, histogram = ta.macd(df)
df["macd"]      = macd_line
df["signal"]    = signal_line
df["histogram"] = histogram

visualize.graph(
    df,
    x="time",
    lines=["close", "ema_21", "ema_50", "macd", "signal"],
    bars=["histogram"],
    title="AAPL - EMA / MACD / RSI",
)
`,
    },
    {
      label: 'Momentum Screener',
      language: 'python',
      filename: 'momentum_screener.py',
      bg: '21.jpg',
      code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

df_tickers = utils.df(client.stocks.tickers(exchange="NASDAQ"))

def fetch_prices(row):
    t = row["symbol"]
    data = client.stocks.prices(t, from_date=utils.d(utils.yearago))
    return (t, utils.df(data))

prices = utils.scribe(
    df_tickers,
    id="nasdaq_prices_ytd",
    cb=fetch_prices,
)

gainers = utils.gainers(prices, frame=252, limit=20)
losers  = utils.losers(prices,  frame=252, limit=20)

visualize.barh(gainers, x="change", y="ticker")
`,
    },
  ]}
/>
      <Marquee/>

      <FeaturesGrid
        title="Everything You Need for Equity Trading. "
        subtitle="Comprehensive stock market data, corporate actions, and deep historical coverage built for long-horizon research and quantitative infrastructure. "
        features={ [
        {
          fig: 'FIG 0.1',
          icon: <Zap size={80} color="var(--purple)" />,
          title: 'Near-Instant Retrieval',
          description: 'Daily and hourly OHLCV data across 85+ exchanges, retrieved nearly instantly. Pull decades of history in milliseconds for backtesting, quantitative research, and long-horizon analysis.',
        },
        {
          fig: 'FIG 0.2',
          icon: <Clock size={80} color="var(--green)" />,
          title: 'Historical Data',
          description: 'Complete price history back to 1987. Daily OHLCV bars for backtesting, charting, and quantitative research on historical stock data.',
        },
        {
          fig: 'FIG 0.3',
          icon: <Database size={80} color="var(--red)" />,
          title: 'Corporate Actions',
          description: 'Comprehensive dividend, split, merger, and spinoff data. Adjust historical prices accurately for corporate events.',
        },
        {
          fig: 'FIG 0.4',
          icon: <TrendingDown size={80} color="var(--purple)" />,
          title: 'Short Interest',
          description: 'Track short interest, days to cover, and short squeeze potential. Essential stock analysis data for understanding market sentiment and positioning.',
        },
        {
          fig: 'FIG 0.5',
          icon: <Activity size={80} color="var(--green)" />,
          title: 'Insider Trading',
          description: 'Monitor insider transactions from Form 4 filings. Track buying and selling activity from executives, directors, and institutional holders.',
        },
        {
          fig: 'FIG 0.6',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global Coverage',
          description: '85+ exchanges across North America, Europe, Asia, and emerging markets. Trade US stocks, international equities, and ADRs from one global stock market API.',
        },
      ]}/>

      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Global Equity Coverage"
        headingPlain="Stock market data"
        headingEm="from 85+ exchanges."
        body="Daily and hourly stock market data from major exchanges across North America, Europe, Asia, and emerging markets. Decades of history back to 1987, normalized and ready for production through a single stock price API."
        stats={[
          { v: "100K+",  l: "Symbols"        },
          { v: "2B+",    l: "Data Points/Day" },
          { v: "35+yr",  l: "History"        },
          { v: "400B+",  l: "Data Points"    },
        ]}
        markers={[
          // North America
          { id: "nyse", location: [40.7069, -74.0113], label: "NYSE" },
          { id: "nasdaq", location: [40.7560, -73.9857], label: "NASDAQ" },
          { id: "amex", location: [40.7065, -74.0110], label: "AMEX" },
          { id: "cboe", location: [41.8781, -87.6298], label: "Cboe" },
          { id: "tsx", location: [43.6487, -79.3803], label: "TSX" },
          { id: "tsxv", location: [43.6487, -79.3803], label: "TSXV" },
          { id: "cse", location: [43.6510, -79.3818], label: "CSE" },
          { id: "neo", location: [43.6487, -79.3803], label: "NEO" },
          { id: "bmv", location: [19.4326, -99.1332], label: "BMV" },
          { id: "biva", location: [19.4326, -99.1332], label: "BIVA" },

          // South America
          { id: "b3", location: [-23.5505, -46.6333], label: "B3 (Brasil)" },
          { id: "byme", location: [-34.6037, -58.3816], label: "BYMA" },
          { id: "bcs", location: [-33.4489, -70.6693], label: "BCS" },
          { id: "bvc", location: [4.7110, -74.0721], label: "BVC" },

          // Europe
          { id: "lse", location: [51.5155, -0.0993], label: "LSE" },
          { id: "lse_aim", location: [51.5155, -0.0993], label: "AIM" },
          { id: "euronext", location: [52.3731, 4.8952], label: "Euronext AMS" },
          { id: "euronext_paris", location: [48.8566, 2.3522], label: "Euronext Paris" },
          { id: "euronext_brussels", location: [50.8503, 4.3517], label: "Euronext Brussels" },
          { id: "euronext_lisbon", location: [38.7223, -9.1393], label: "Euronext Lisbon" },
          { id: "deutsche", location: [50.1138, 8.6798], label: "Xetra" },
          { id: "fse", location: [50.1109, 8.6821], label: "Frankfurt" },
          { id: "swb", location: [48.7758, 9.1829], label: "Stuttgart" },
          { id: "ber", location: [52.5200, 13.4050], label: "Berlin" },
          { id: "ham", location: [53.5511, 9.9937], label: "Hamburg" },
          { id: "mun", location: [48.1351, 11.5820], label: "Munich" },
          { id: "dus", location: [51.2277, 6.7735], label: "Düsseldorf" },
          { id: "han", location: [52.3759, 9.7320], label: "Hanover" },
          { id: "six", location: [47.3719, 8.5404], label: "SIX Swiss" },
          { id: "bme", location: [40.4168, -3.7038], label: "BME" },
          { id: "borsa_italiana", location: [45.4642, 9.1900], label: "Borsa Italiana" },
          { id: "nasdaq_nordic", location: [59.3293, 18.0686], label: "Nasdaq Nordic" },
          { id: "oslo", location: [59.9139, 10.7522], label: "Oslo Børs" },
          { id: "iceland", location: [64.1466, -21.9426], label: "Iceland SE" },
          { id: "warsaw", location: [52.2297, 21.0122], label: "WSE" },
          { id: "prague", location: [50.0755, 14.4378], label: "Prague SE" },
          { id: "vienna", location: [48.2082, 16.3738], label: "Vienna SE" },
          { id: "budapest", location: [47.4979, 19.0402], label: "Budapest SE" },
          { id: "bucharest", location: [44.4268, 26.1025], label: "BVB" },
          { id: "sofia", location: [42.6977, 23.3219], label: "BSE Sofia" },
          { id: "zagreb", location: [45.8150, 15.9819], label: "ZSE" },
          { id: "belgrade", location: [44.7866, 20.4489], label: "Belgrade SE" },
          { id: "ljubljana", location: [46.0569, 14.5058], label: "Ljubljana SE" },
          { id: "tallinn", location: [59.4370, 24.7536], label: "Tallinn SE" },
          { id: "riga", location: [56.9496, 24.1052], label: "Riga SE" },
          { id: "vilnius", location: [54.6872, 25.2797], label: "Vilnius SE" },
          { id: "malta", location: [35.8989, 14.5146], label: "Malta SE" },
          { id: "cyprus", location: [35.1667, 33.3667], label: "Cyprus SE" },

          // Russia & CIS
          { id: "moex", location: [55.7558, 37.6173], label: "MOEX" },
          { id: "spb", location: [59.9343, 30.3351], label: "SPB Exchange" },
          { id: "kase", location: [51.1605, 71.4704], label: "KASE" },

          // Africa
          { id: "jse", location: [-26.2041, 28.0473], label: "JSE" },
          { id: "egypt", location: [30.0444, 31.2357], label: "EGX" },
          { id: "casablanca", location: [33.5731, -7.5898], label: "Casablanca SE" },
          { id: "nse_nigeria", location: [6.5244, 3.3792], label: "NSE Nigeria" },
          { id: "brvm", location: [-18.8792, 47.5079], label: "BRVM" },
          { id: "mauritius", location: [-20.1667, 57.5000], label: "SEM" },
          { id: "botswana", location: [-24.6282, 25.9231], label: "BSE" },
          { id: "namibia", location: [-22.5609, 17.0658], label: "NSX" },
          { id: "ghana", location: [5.6037, -0.1870], label: "GSE" },
          { id: "kenya", location: [-1.2864, 36.8172], label: "NSE Kenya" },
          { id: "rwanda", location: [-1.9441, 30.0619], label: "RSE" },
          { id: "tanzania", location: [-6.7924, 39.2083], label: "DSE" },
          { id: "uganda", location: [0.3136, 32.5811], label: "USE" },
          { id: "zambia", location: [-15.4167, 28.2833], label: "LuSE" },
          { id: "zimbabwe", location: [-17.8292, 31.0539], label: "ZSE" },

          // Middle East
          { id: "tadawul", location: [24.7136, 46.6753], label: "Tadawul" },
          { id: "dfm", location: [25.2048, 55.2708], label: "DFM" },
          { id: "adx", location: [24.4539, 54.3773], label: "ADX" },
          { id: "kuwait", location: [29.3759, 47.9774], label: "Boursa Kuwait" },
          { id: "qatar", location: [25.2854, 51.5310], label: "QE" },
          { id: "bahrain", location: [26.2285, 50.5860], label: "BHB" },
          { id: "muscat", location: [23.5880, 58.3829], label: "MSM" },
          { id: "tase", location: [32.0853, 34.7818], label: "TASE" },
          { id: "amman", location: [31.9454, 35.9284], label: "ASE" },
          { id: "beirut", location: [33.8938, 35.5018], label: "BSE" },
          { id: "tehran", location: [35.6892, 51.3890], label: "TSE" },
          { id: "iraq", location: [33.3152, 44.3661], label: "ISX" },

          // Asia - South
          { id: "bse", location: [18.9292, 72.8339], label: "BSE" },
          { id: "nse", location: [19.0760, 72.8777], label: "NSE" },
          { id: "cse_colombo", location: [6.9271, 79.8612], label: "CSE" },
          { id: "dse_bangladesh", location: [23.8103, 90.4125], label: "DSE" },
          { id: "pakistan", location: [24.8607, 67.0011], label: "PSX" },

          // Asia - China & Greater China
          { id: "sse", location: [31.2361, 121.5063], label: "SSE" },
          { id: "szse", location: [22.5392, 114.0497], label: "SZSE" },
          { id: "hkex", location: [22.2796, 114.1612], label: "HKEX" },
          { id: "taiwan", location: [25.0330, 121.5654], label: "TWSE" },
          { id: "tpex", location: [25.0330, 121.5654], label: "Taipei Exchange" },

          // Asia - Japan & Korea
          { id: "tse", location: [35.6825, 139.7731], label: "TSE" },
          { id: "nagoya", location: [35.1815, 136.9066], label: "Nagoya SE" },
          { id: "fukuoka", location: [33.5904, 130.4017], label: "Fukuoka SE" },
          { id: "sapporo", location: [43.0618, 141.3545], label: "Sapporo SE" },
          { id: "krx", location: [37.5226, 127.0453], label: "KRX" },
          { id: "kosdaq", location: [37.5226, 127.0453], label: "KOSDAQ" },

          // Asia - Southeast
          { id: "sgx", location: [1.2776, 103.8519], label: "SGX" },
          { id: "set", location: [13.7367, 100.5231], label: "SET" },
          { id: "mai", location: [13.7367, 100.5231], label: "MAI" },
          { id: "idx", location: [-6.2088, 106.8456], label: "IDX" },
          { id: "pse", location: [14.5995, 120.9842], label: "PSE" },
          { id: "hose", location: [10.8231, 106.6297], label: "HoSE" },
          { id: "hnx", location: [21.0285, 105.8542], label: "HNX" },
          { id: "klse", location: [3.1390, 101.6869], label: "Bursa Malaysia" },
          { id: "ysx", location: [16.8409, 96.1735], label: "YSX" },

          // Asia - Central & Mongolia
          { id: "mse", location: [47.9188, 106.9179], label: "MSE" },

          // Oceania
          { id: "asx", location: [-33.8625, 151.2105], label: "ASX" },
          { id: "nzx", location: [-36.8485, 174.7633], label: "NZX" },
          { id: "pomsox", location: [-9.4438, 147.1803], label: "PNGX" },

          // Caribbean & Offshore
          { id: "ttse", location: [10.6549, -61.5090], label: "TTSE" },
          { id: "jse_jamaica", location: [17.9714, -76.7934], label: "JSE" },
          { id: "bsx", location: [25.0343, -77.3963], label: "BSX" }
        ]}
      />

      <DataGrid
        title="Equity Intelligence: The Complete Mosaic"
        subtitle="Fuse fundamentals, pricing, alternative data, and market microstructure into a single, clean dataset. The definitive dataset for long-horizon analysis, quantitative modeling, and alpha generation."
        bg={databg}
        stats={[
          { label: 'Global Equities', value: '100,000+' },
          { label: 'Data Points per Day', value: '2B+' },
          { label: 'Alternative Datasets', value: '50+' },
        ]}
      />
      <CardList
        title="Engineered for Alpha Discovery"
        vid={'https://cdn.studio14a.com/axion/products/cards/stocks.mp4'}
        points={[
          {
            "title": "Clean Fundamentals & Pricing",
            "details": "Access decades of adjusted historical prices, dividends, splits, and fully normalized financial statements-restated for comparability and mapped to precise reporting dates.",
            "cta": "Explore core equity data",
            "url": "/docs"
          },
          {
            "title": "Integrated Alternative Data Signals",
            "details": "Weave together web traffic, sentiment, supply chain, credit, and ESG data directly into your equity models. Discover non-obvious relationships in a structured format.",
            "cta": "View alternative datasets",
            "url": "/products/sentiment"
          },
          {
            "title": "Market Microstructure & Liquidity",
            "details": "Model execution impact with volume profiles, liquidity metrics, and short interest data. Understand the true liquidity and trading dynamics of any stock.",
            "cta": "Analyze market structure",
            "url": "/products/etfs"
          }
        ]}
      />

      <Responses
        heading='Institutional-Grade Data'
        subheading='The foundation for quantitative research and trading systems'
        ticker='AAPL'
        tabs={[
          {
            title: 'Ticker Details',
            description: 'Core stock metadata – name, exchange, sector, industry, and currency for a given ticker',
            data: {
              "id": 17640,
              "name": "Apple Inc.",
              "ticker": "AAPL",
              "symbol": "NASDAQ:AAPL",
              "exchange": "NASDAQ",
              "market": "america",
              "country": "United States",
              "type": "common stock",
              "sector": "Electronic Technology",
              "industry": "Telecommunications Equipment",
              "currency": "USD",
              "lastClose": 297.78500366,
              "changePct": 0.260931909533994
            }
          },

          {
            title: 'Price Snapshot',
            description: 'Current trading stats, 52-week range, and valuation multiples',
            data:{
              "latestClose": 297.78500366,
              "latestOpen": 297.53799438,
              "latestHigh": 301.64001465,
              "latestLow": 295.17999268,
              "previousClose": 297.01000977,
              "week52Range": {
                "high": 317.4,
                "low": 199.26
              },
              "week52AvgClose": 258.3188,
              "ema30": 296.4199,
              "ema60": 288.4353,
              "ema90": 282.4145,
              "ema120": 277.6401,
              "ytdClosePctChange": 9.8797,
              "volume": 24818893,
              "avgVolumeLifetime": 307036937.9755
            },
          },
          {
            title: 'Historical Prices',
            description: 'Daily OHLCV price data for a stock ticker with volume',
            data:[
              {
                "time": "2026-06-23T00:00:00.000Z",
                "ticker": "AAPL",
                "open": "297.53799438",
                "high": "301.64001465",
                "low": "295.17999268",
                "close": "297.78500366",
                "volume": "24818893"
              },
              {
                "time": "2026-06-22T00:00:00.000Z",
                "ticker": "AAPL",
                "open": "297.30999756",
                "high": "302.42001343",
                "low": "296.76000977",
                "close": "297.01000977",
                "volume": "44812800"
              },
              {
                "time": "2026-06-18T00:00:00.000Z",
                "ticker": "AAPL",
                "open": "298.10998535",
                "high": "300.57000732",
                "low": "295.61999512",
                "close": "298.01000977",
                "volume": "85962200"
              }],
          },


          {
            title: 'Calendar Events',
            description: 'Upcoming earnings dates, earnings call dates, and dividend calendar with consensus estimates.',
            data: {
              earnings: {
                earningsDate: ["2026-01-29T21:00:00.000Z"],
                earningsCallDate: ["2025-10-30T21:00:00.000Z"],
                isEarningsDateEstimate: true,
                earningsAverage: 2.6647,
                earningsLow: 2.51,
                earningsHigh: 2.76,
                revenueAverage: 138253076640,
                revenueLow: 136679500000,
                revenueHigh: 142741000000,
              },
              exDividendDate: "2025-11-10T00:00:00.000Z",
              dividendDate: "2025-11-13T00:00:00.000Z",
            },
          },
          {
            title: 'Financial Metrics',
            description: 'A single-call composite of the latest fiscal year - revenue, income, balance sheet, cash flow, and derived ratios in camelCase.',
            data: {
              revenue: 143756000000,
              netIncome: 42097000000,
              totalAssets: 379297000000,
              totalLiabilities: 291107000000,
              stockholdersEquity: 88190000000,
              currentAssets: 158104000000,
              currentLiabilities: 162367000000,
              operatingCashFlow: 53925000000,
              capitalExpenditures: 2373000000,
              freeCashFlow: 51552000000,
              sharesOutstandingBasic: 14748158000,
              sharesOutstandingDiluted: 14810356000,
              currentRatio: 0.973744664864166,
              debtToAssets: 0.76749091081659
            },
          },
          {
            title: 'Financial Snapshot',
            description: 'Latest market data: current price, analyst targets, margins, growth rates, and valuation ratios, updated daily.',
            data: {
              currentPrice: 272.19,
              targetHighPrice: 350,
              targetLowPrice: 215,
              targetMeanPrice: 287.70682,
              targetMedianPrice: 300,
              recommendationMean: 2,
              recommendationKey: 'buy',
              numberOfAnalystOpinions: 41,
              totalCash: 54697000960,
              totalCashPerShare: 3.702,
              ebitda: 144748003328,
              totalDebt: 112377004032,
              quickRatio: 0.771,
              currentRatio: 0.893,
              totalRevenue: 416161005568,
              debtToEquity: 152.411,
              revenuePerShare: 27.84,
              returnOnAssets: 0.22964,
              returnOnEquity: 1.7142199,
              grossProfits: 195201007616,
              freeCashflow: 78862254080,
              operatingCashflow: 111482003456,
              earningsGrowth: 0.912,
              revenueGrowth: 0.079,
              grossMargins: 0.46905,
              ebitdaMargins: 0.34782,
              operatingMargins: 0.31647,
              profitMargins: 0.26915002,
              financialCurrency: 'USD'
            },
          },
          {
            title: 'Company News',
            description: 'Current headlines tied directly to a ticker, pulled from financial and general news sources and ready to feed straight into your pipeline.',
            data: [
              {
                title: "Apple spent 2025 setting itself up for the future - and its biggest moves weren't about AI - Yahoo Finance",
                link: "https://api.axionquant.com/news/article/aHR0cHM6Ly9uZXdzLmdvb2dsZS5jb20vcnNzL2FydGljbGVzL0NCTWl6d0ZCVlY5NWNVeE5OblZDV1VwbVMxTnhaVlZRYVd4VlRqQk9XR1ZOTWxFM1UxaFdiRjlXYld4elNDMWlVVk5IYWxKT1pFbGhWbmc1TlZKRlEycHJNMHBKYjNGeGMyUXlPRTAzZGxGNFRGaFRiVUZWTUhveGRsRk9Va2M1YmxKV2R6RTFSbkV4VDBKV1pHTmtNMVJ6TVRsV00xaFZjbWRwYVRsUFJYbEdRbGRMU3pWMFVrZGZUVFJzZFdkWE9Vb3lUSGgyT0hZMlZWQTROM2R5YUhkb1pVczJiM0JLVVV0d2FuUk5RalpKTUZacVpITm9WamhXVEhOamVVaG9WMnhMZGt4dFUxWk1hV2szVlZaUlJ6bENhekE_b2M9NQ",
                summary: "Apple spent 2025 setting itself up for the future - and its biggest moves weren't about AI  Yahoo Finance",
                published: "Wed, 24 Dec 2025 09:05:43 GMT"
              },
              {
                title: "Apple Just Released a New AI Model. Should You Buy AAPL Stock Here? - Barchart.com",
                link: "https://api.axionquant.com/news/article/aHR0cHM6Ly9uZXdzLmdvb2dsZS5jb20vcnNzL2FydGljbGVzL0NCTWlyd0ZCVlY5NWNVeE9aRVV6TkZoTWJqbFpiRzV2YTI4MlFsUnFjMGhxVldSbU0waFZWek5SU0d4WlRsRkdaRWg0YW05Rk9IcFBUbFE0WTNJMlNDMUxOMk5oYjJ4SGEzQktNMGhqUmpkd2NWOUVWelpWY3pCZmFWcHVhemhFVTA5RFpuSlBlbkJEV0hGV2FtNUxXV1ZaTW1GUE9GWkljSGxzU0hWNGFrZHFiVk41U2psQ2RFOXplRk52YmpVM1dXSTJTa3hWWVZZd1VsVmZTM1pCY21wR05ubHdTWEY2T1RneGIwbHBNVWhLYVZWSj9vYz01",
                summary: "Apple Just Released a New AI Model. Should You Buy AAPL Stock Here?  Barchart.com",
                published: "Tue, 23 Dec 2025 16:00:03 GMT"
              }
            ],
          },

        ]}
      />
      <DatasetDetails full={true}
        title="Global Equity Coverage, Institutional Precision"
        subtitle="Tap into daily and hourly OHLCV data from 85+ exchanges, covering over 100,000 stocks with full historical depth back to 1987. Pull decades of price history nearly instantly, with normalized corporate actions and integrated signals for short interest and insider trading, all through a single, clean stock price API. Purpose-built for long-horizon research, risk management, portfolio analytics, and quantitative investment research."
        cta="Get Started Free"
        ctaLink="/login"
        tiles={[
          {
            img: '6.webp',
            title: 'We cover over 100,000 symbols',
            caption: 'You get access to daily and hourly data for stocks, ETFs, and more across all major global exchanges.',
            cn: 'scaled',
            tile: <W1/>
          },
          {
            img: '14.webp',
            title: 'We connect to 85+ global exchanges',
            caption: 'You can access daily and hourly market data from every major trading venue worldwide.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '1.webp',
            title: 'We provide 400B+ data points',
            caption: 'You can power quantitative research and backtesting with an immense corpus of historical daily and hourly data.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '9.webp',
            title: 'We offer 35+ years of history',
            caption: 'You can analyze daily and hourly OHLCV data going back to 1987 for deep historical and quantitative research.',
            cn: 'scaled corner',
            tile: <W4/>
          },
          {
            img: '4.webp',
            title: 'We retrieve data in milliseconds',
            caption: 'You can pull decades of history nearly instantly to power long-horizon research and backtesting.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />
      <Outcomes
        headline="Numbers that speak for themselves."
        subheadline="Institutional-grade data trusted by hedge funds, asset managers, and fintech companies worldwide."
        leftCell={{ stat: '400B+', title: '400B+ data points.', desc: 'Institutional-grade data across 85+ global exchanges.', showChart: true }}
        rightCells={[
          { stat: '2B+', title: '2B+ Data Points a Day.', desc: 'Daily and hourly data across 85+ global exchanges.' },
          { stat: '35+yr', title: '35+ Years of History.', desc: 'Daily and hourly data back to 1987, retrievable nearly instantly.', boldDesc: true },
        ]}
      /><ProductFAQ slug="stocks" />


      <PricingTable />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default StocksPage;

